// ============================================================================
// One agent, one state (2026-10-10): every surface that names an agent's state
// (the Now page, the Agents screen and column, the strip on a phone, Stats, a
// thread's header, a task's agents, the project board, the Graph) reads it from
// here, so "Your turn" in one place is never "On Prime" in another. The model is
// lib/waiting.ts; the runs and the lead's cards are read in one query
// (useRunsAndLeadCards), so a page never draws the runs without their cards.
// ============================================================================
import { useMemo } from 'react'
import { useRunsAndLeadCards } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import type { Builds } from '@/db/api'
import type { AgentRunResource } from '@/kinds/agentrun'
import { runStatus, waitingOf, type Waiting } from '@/lib/waiting'
import { Tag } from './ui'

const isLeadRun = (r: AgentRunResource) => r.spec.template === 'prime'

/** Everything in flight. `builds` adds builds and prod when the caller has them. */
export const useWaiting = (
  builds?: Builds | null,
): { items: Waiting[]; lead?: AgentRunResource; now: number } => {
  const read = useRunsAndLeadCards()
  const now = useNow(30_000)
  return useMemo(() => {
    const runs = read?.runs ?? []
    const lead = runs.find((r) => r.id === read?.leadId)
    return { items: waitingOf({ runs, cards: read?.cards ?? [], builds, now }), lead, now }
  }, [read, builds, now])
}

/** Each agent's chip by its id: who holds its work, else its phase (`runStatus`). */
export const useRunStatus = () => {
  const { items } = useWaiting()
  return useMemo(() => {
    const byRun = new Map(items.flatMap((w) => (w.run ? [[w.run.id, w] as const] : [])))
    return (run: AgentRunResource, phase = run.spec.status) =>
      runStatus(phase, byRun.get(run.id), isLeadRun(run))
  }, [items])
}

/** The chip itself, for a list that draws one agent per row. */
export const RunTag = ({ run, phase }: { run: AgentRunResource; phase?: string }) => {
  const statusOf = useRunStatus()
  const s = statusOf(run, phase)
  return <Tag tone={s.tone}>{s.word}</Tag>
}
